import moment from 'moment';
import { JobPostingStatus, JobPostingStatusMapper, PayType, ScheduleType } from '../enums';
import {
  IDraftJobPostListViewEntity,
  IGetJobPostingsPayload,
  IGetJobPostingsResult,
  IJobPostApiResponse,
  IJobPostEntity,
  IGetJobPostDetailResult,
  IJobPostDetailEntity,
  IGetJobPostDetailApiResponse,
} from '../types';
import { IOffsetPaginationMapper } from '~/types/Pagination';
import { ProfessionTypeEnum } from '~/models/Profession';

const toGetJobPostingsApiQueryParams = ({
  limit,
  officeId,
  orderBy,
  orderDirection,
  page,
  professionType,
  subProfessionType,
  status,
}: IGetJobPostingsPayload): Record<string, any> => {
  const params = {
    officeId,
    orderBy,
    orderDirection,
    page,
    pageSize: limit,
    professionType,
    status,
    subProfession: subProfessionType,
  };

  return Object.fromEntries(Object.entries(params).filter(([, value]) => !!value));
};

const toGetJobPostingsResult = (response: IJobPostApiResponse): IGetJobPostingsResult => {
  const format = 'MMMM D, YYYY';

  return {
    data: response.data.map((it): IJobPostEntity | IDraftJobPostListViewEntity => {
      if (it.status === JobPostingStatus.Draft) {
        return {
          id: it.id,
          lastSavedAt: moment(it.updatedAt).format(format),
          office: it.officeId
            ? {
                id: it.officeId,
                name: it.office?.name || '',
              }
            : null,
          officeSelected: it.officeSelected,
          professionType: it.professionType,
          status: it.status,
          subProfession: it.subProfession,
          title: it.title,
        } satisfies IDraftJobPostListViewEntity;
      } else {
        return {
          candidateCount: it.candidateCount,
          closedAt: it.closedAt ? moment(it.closedAt).format(format) : null,
          hires: it.hires,
          id: it.id,
          office: {
            id: it.officeId!,
            name: it.office?.name || '',
          },
          professionType: it.professionType!,
          publishedAt: it.publishedAt
            ? moment(it.publishedAt).format(format)
            : moment(it.createdAt).format(format),
          status: it.status,
          title: it.title!,
          onsiteInterviewsCount: it.onsiteInterviewsCount,
          subProfession: it.subProfession,
        } satisfies IJobPostEntity;
      }
    }),
    pagination: IOffsetPaginationMapper.fromApi(response.metadata.pagination),
    statistics: {
      tabs: response.metadata.tabs,
    },
  } satisfies IGetJobPostingsResult;
};

/**
 * Transforms API response to job post detail entity for viewing published posts.
 * Unlike toGetDraftJobPostResult, this assumes all required fields are populated.
 */
const toGetJobPostDetailResult = (
  response: IGetJobPostDetailApiResponse,
): IGetJobPostDetailResult => {
  return {
    data: {
      benefits: response.data.benefits,
      benefitsOther: response.data.benefitsOther,
      closedAt: response.data.closedAt,
      closedBy: response.data.closedBy,
      createdAt: response.data.createdAt || '',
      createdBy: response.data.createdBy,
      description: response.data.description || '',
      expectedDays: response.data.expectedDays,
      id: response.data.id,
      maxRate: response.data.salaryRange?.max,
      minRate: response.data.salaryRange?.min,
      officeId: response.data.officeId,
      officeName: response.data.officeName,
      officeSelected: response.data.officeSelected,
      officeSpecialty: response.data.specialty,
      payType: response.data.salaryRange?.payType as PayType,
      professionType: response.data.professionType as ProfessionTypeEnum,
      publishedAt: response.data.publishedAt || '',
      scheduleType: response.data.scheduleType as ScheduleType,
      status: JobPostingStatusMapper.safeCast(response.data.status) as JobPostingStatus,
      subProfession: response.data.subProfession,
      title: response.data.title || '',
      updatedAt: response.data.updatedAt || '',
      updatedBy: response.data.updatedBy,
      yearlyRate: response.data.salaryRange?.yearly,
      planId: response.data.planId,
      officePlanId: response.data.officePlanId,
      candidateCount: response.data.candidateCount,
      officeState: response.data.officeState,
      planName: response.data.planName,
      suspendedAt: response.data.suspendedAt,
    } satisfies IJobPostDetailEntity,
  } satisfies IGetJobPostDetailResult;
};

export const JobPostAdapters = {
  toGetJobPostingsApiQueryParams,
  toGetJobPostingsResult,
  toGetJobPostDetailResult,
};
